import React, { useState, createContext, useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';
import ReactCountryFlag from 'react-country-flag';
import { CartContext } from '../App';
import { getTranslation, Language } from '../translations';
import { Wrench } from 'lucide-react';

// Maak een context voor taalwisseling
export const LanguageContext = createContext<{
  language: string;
  setLanguage: (lang: string) => void | Promise<void>;
  isLoading?: boolean;
  t?: (key: string) => string;
}>({
  language: 'nl',
  setLanguage: () => { },
  isLoading: false,
  t: (key: string) => key
});

// Hook om de taal context te gebruiken
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  return {
    language: context.language || 'nl',
    setLanguage: context.setLanguage,
    isLoading: context.isLoading || false,
    t: context.t || ((key: string) => getTranslation(context.language as Language, key))
  };
};

// Context voor navigatie tussen pagina's
export const NavigationContext = createContext({
  goToHome: () => { },
  goToCart: () => { }
});

// Hook om de navigatie context te gebruiken
export const useNavigation = () => useContext(NavigationContext);

// Talen configuratie
const languages = [
  { code: 'nl', countryCode: 'nl', name: 'Nederlands' },
  { code: 'en', countryCode: 'gb', name: 'English' },
  { code: 'fr', countryCode: 'fr', name: 'Français' },
  { code: 'de', countryCode: 'de', name: 'Deutsch' },
  { code: 'es', countryCode: 'es', name: 'Español' },
  { code: 'tr', countryCode: 'tr', name: 'Türkçe' },
  { code: 'bg', countryCode: 'bg', name: 'Български' },
  { code: 'ro', countryCode: 'ro', name: 'Română' },
  { code: 'sk', countryCode: 'sk', name: 'Slovenčina' }
];

// Sticky header container with scroll effects + hide-on-scroll
const HeaderContainer = styled.header<{ $isScrolled: boolean; $isHidden: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  justify-content: center;
  pointer-events: none;
  padding: ${({ $isScrolled }) => ($isScrolled ? '8px 12px' : '24px 12px')};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease, padding 0.3s ease;
  transform: ${({ $isHidden }) => ($isHidden ? 'translateY(-150%)' : 'translateY(0)')};
  opacity: ${({ $isHidden }) => ($isHidden ? 0 : 1)};
  will-change: transform, opacity;

  &::before {
    content: none;
  }

  .dark &::before {
    content: none;
  }

  &::after {
    content: none;
  }

  .dark &::after {
    content: none;
  }

  @media (max-width: 768px) {
    top: 12px;
    padding: 0 12px;
  }

  @media (max-width: 480px) {
    top: 8px;
    padding: 0 10px;
  }
`;

const HeaderShell = styled.div<{ $isLangOpen: boolean; $isScrolled: boolean }>`
  background: #ffffff;
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
  border-radius: 9999px;
  padding: ${({ $isScrolled }) => ($isScrolled ? '0.5rem 0.9rem' : '0.6rem 1rem')};
  width: 100%;
  max-width: min(1240px, calc(100% - 48px));
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: all;
  overflow: ${({ $isLangOpen }) => ($isLangOpen ? 'visible' : 'hidden')};
  transform: ${({ $isScrolled }) => ($isScrolled ? 'scale(0.98)' : 'scale(1)')};

  &:hover {
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
  }

  .dark & {
    background: #0f172a;
    border: 1px solid rgba(71, 85, 105, 0.3);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);

    &:hover {
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
    }
  }

  @media (max-width: 1320px) {
    max-width: min(1160px, calc(100% - 40px));
    border-radius: 28px;
  }

  @media (max-width: 768px) {
    max-width: calc(100% - 24px);
    border-radius: 20px;
    padding: 0.4rem 0.65rem;
  }

  @media (max-width: 480px) {
    max-width: calc(100% - 16px);
    border-radius: 18px;
    padding: 0.35rem 0.5rem;
  }
`;

const HeaderContent = styled.div<{ $isScrolled: boolean }>`
  padding: ${({ $isScrolled }) => ($isScrolled ? '0.6rem 1.5rem' : '0.8rem 2rem')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: ${({ $isScrolled }) => ($isScrolled ? '52px' : '62px')};
  gap: ${({ $isScrolled }) => ($isScrolled ? '1.5rem' : '1.75rem')};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 1024px) {
    padding: 0.75rem 1.75rem;
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0.9rem;
    gap: 0.75rem;
    min-height: 52px;
  }

  @media (max-width: 480px) {
    padding: 0.4rem 0.65rem;
    gap: 0.5rem;
    min-height: 48px;
  }
`;

// Minimal inline icons (stroke uses currentColor)
const BagIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 9l1 11h10l1-11H6z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 9V7a3 3 0 116 0v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BookmarkIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 4h10a1 1 0 011 1v15l-6-3-6 3V5a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const UserIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 12a4 4 0 100-8 4 4 0 000 8z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4.5 20a7.5 7.5 0 0115 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TrendIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 17l6-6 4 4 7-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15 8h5v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TagIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.2s ease;
  
  @media (max-width: 768px) {
    width: 100%;
    height: 100%;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
  
  @media (max-width: 480px) {
    svg {
      width: 18px;
      height: 18px;
    }
  }
  
  @media (max-width: 360px) {
    svg {
      width: 16px;
      height: 16px;
    }
  }
  
  button:hover & {
    transform: scale(1.08) rotate(-5deg);
    
    @media (max-width: 768px) {
      transform: scale(1.05);
    }
  }
`;

const Logo = styled.a<{ $isScrolled: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ $isScrolled }) => ($isScrolled ? '0.5rem' : '0.6rem')};
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${({ $isScrolled }) => ($isScrolled ? 'scale(0.95)' : 'scale(1)')};
  flex-shrink: 0;

  @media (max-width: 480px) {
    gap: ${({ $isScrolled }) => ($isScrolled ? '0.4rem' : '0.5rem')};
  }
`;

const LogoImage = styled.img<{ $isScrolled: boolean }>`
  height: ${({ $isScrolled }) => ($isScrolled ? '20px' : '24px')};
  transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 480px) {
    height: ${({ $isScrolled }) => ($isScrolled ? '20px' : '22px')};
  }

  @media (max-width: 360px) {
    height: ${({ $isScrolled }) => ($isScrolled ? '18px' : '20px')};
  }
`;

const LogoText = styled.span<{ $isScrolled: boolean }>`
  font-size: ${({ $isScrolled }) => ($isScrolled ? '1rem' : '1.125rem')};
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.025em;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  .dark & {
    color: #ffffff;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    display: none; // Verberg logo tekst op zeer kleine schermen
  }

  @media (max-width: 360px) {
    font-size: 0.9rem;
  }
`;

const Navigation = styled.nav<{ $isScrolled: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ $isScrolled }) => ($isScrolled ? '0.6rem' : '0.75rem')};
  transition: gap 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 480px) {
    gap: 0.4rem;
    flex-wrap: nowrap;
  }

  @media (max-width: 360px) {
    gap: 0.3rem;
  }
`;

// Theme toggle button (sun/moon)
const ThemeToggleButton = styled.button<{ $isScrolled: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $isScrolled }) => ($isScrolled ? '36px' : '40px')};
  height: ${({ $isScrolled }) => ($isScrolled ? '36px' : '40px')};
  border-radius: 9999px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: #f8fafc;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${({ $isScrolled }) => ($isScrolled ? 'scale(0.95)' : 'scale(1)')};
  color: #1f2937;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: #f1f5f9;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  svg { width: 18px; height: 18px; }

  .dark & {
    background: #1e293b;
    color: #e2e8f0;
    border: 1px solid rgba(148, 163, 184, 0.3);
    
    &:hover {
      background: #334155;
    }
  }

  @media (max-width: 768px) {
    display: none; // Verberg theme toggle op mobiel
  }
`;

const FlagWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 9999px;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05);
  background: #ffffff;
  
  img,
  svg {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 9999px;
  }
  
  .dark & {
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const LanguageSelector = styled.div`
  position: relative;
`;

const LanguageButton = styled.button<{ $isScrolled: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 9999px;
  padding: ${({ $isScrolled }) => ($isScrolled ? '0.45rem 0.875rem' : '0.5rem 1rem')};
  font-size: ${({ $isScrolled }) => ($isScrolled ? '0.85rem' : '0.875rem')};
  font-weight: 600;
  color: #1f2937;
  transition: all 0.2s ease;
  transform: ${({ $isScrolled }) => ($isScrolled ? 'scale(0.95)' : 'scale(1)')};
  cursor: pointer;
  position: relative;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;

  @media (max-width: 480px) {
    padding: ${({ $isScrolled }) => ($isScrolled ? '0.4rem 0.7rem' : '0.45rem 0.85rem')};
    font-size: ${({ $isScrolled }) => ($isScrolled ? '0.8rem' : '0.85rem')};
    gap: 0.4rem;
  }

  @media (max-width: 360px) {
    padding: ${({ $isScrolled }) => ($isScrolled ? '0.35rem 0.6rem' : '0.4rem 0.7rem')};
    gap: 0.35rem;
  }

  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0) ${({ $isScrolled }) => ($isScrolled ? 'scale(0.95)' : 'scale(1)')};
  }

  .dark & {
    background: #1e293b;
    border: 1px solid rgba(71, 85, 105, 0.6);
    color: #e2e8f0;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    
    &:hover {
      background: #334155;
      border-color: rgba(100, 116, 139, 0.7);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
    }
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  }
`;

const DropdownIcon = styled.svg<{ $isOpen?: boolean }>`
  width: 1rem;
  height: 1rem;
  color: #94a3b8;
  transition: transform 0.2s ease;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  flex-shrink: 0;

  .dark & {
    color: #64748b;
  }
`;

const LanguageDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 0.5rem;
  z-index: 60;
  width: 200px;
  opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
  transform: ${({ $isOpen }) => ($isOpen ? 'translateY(0)' : 'translateY(-5px)')};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  transition: all 0.2s ease;
  overflow: hidden;

  @media (max-width: 480px) {
    width: 180px;
    padding: 0.4rem;
    right: -8px;
  }

  @media (max-width: 360px) {
    width: 160px;
    padding: 0.35rem;
  }

  .dark & {
    background: #1e293b;
    border: 1px solid rgba(71, 85, 105, 0.6);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  }
`;

const LanguageOption = styled.button<{ selected?: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem;
  border-radius: 8px;
  border: ${({ selected }) => (selected ? '1px solid rgba(37,99,235,0.3)' : '1px solid transparent')};
  background: ${({ selected }) => (selected ? 'rgba(37,99,235,0.08)' : 'transparent')};
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background-color: rgba(37, 99, 235, 0.1);
    
    .dark & {
      background-color: rgba(37, 99, 235, 0.15);
    }
  }

  .dark & {
    border: ${({ selected }) => (selected ? '1px solid rgba(59,130,246,0.4)' : '1px solid transparent')};
    background: ${({ selected }) => (selected ? 'rgba(59,130,246,0.12)' : 'transparent')};
  }
`;

const LanguageName = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #334155;
  transition: color 0.2s ease;
  flex: 1;
  
  .dark & {
    color: #e2e8f0;
  }
`;

const LanguageCode = styled.span`
  font-size: 0.8125rem;
  color: #94a3b8;
  transition: color 0.3s ease;
  
  .dark & {
    color: #64748b;
  }
`;

/* Removed checkmark indicator per design – border highlight is sufficient */

const CartButton = styled.button<{ $isScrolled: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ $isScrolled }) => ($isScrolled ? '0.55rem' : '0.65rem')};
  background: #111827;
  border: 1px solid transparent;
  color: #ffffff;
  border-radius: 9999px;
  padding: ${({ $isScrolled }) => ($isScrolled ? '0.5rem 1.15rem' : '0.6rem 1.4rem')};
  font-size: ${({ $isScrolled }) => ($isScrolled ? '0.85rem' : '0.9375rem')};
  font-weight: 700;
  letter-spacing: -0.01em;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  transform: ${({ $isScrolled }) => ($isScrolled ? 'scale(0.95)' : 'scale(1)')};
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
  position: relative;
  overflow: visible;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: ${({ $isScrolled }) => ($isScrolled ? '0.5rem' : '0.6rem')};
    width: ${({ $isScrolled }) => ($isScrolled ? '44px' : '48px')};
    height: ${({ $isScrolled }) => ($isScrolled ? '44px' : '48px')};
    min-width: ${({ $isScrolled }) => ($isScrolled ? '44px' : '48px')};
    gap: 0;
    justify-content: center;
    overflow: visible;
  }

  @media (max-width: 480px) {
    padding: ${({ $isScrolled }) => ($isScrolled ? '0.45rem' : '0.5rem')};
    width: ${({ $isScrolled }) => ($isScrolled ? '40px' : '44px')};
    height: ${({ $isScrolled }) => ($isScrolled ? '40px' : '44px')};
    min-width: ${({ $isScrolled }) => ($isScrolled ? '40px' : '44px')};
    gap: 0;
    overflow: visible;
  }

  @media (max-width: 360px) {
    padding: ${({ $isScrolled }) => ($isScrolled ? '0.4rem' : '0.45rem')};
    width: ${({ $isScrolled }) => ($isScrolled ? '38px' : '42px')};
    height: ${({ $isScrolled }) => ($isScrolled ? '38px' : '42px')};
    min-width: ${({ $isScrolled }) => ($isScrolled ? '38px' : '42px')};
    gap: 0;
    overflow: visible;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.25s ease;
  }

  &:hover {
    background: #000000;
    transform: translateY(-1px) ${({ $isScrolled }) => ($isScrolled ? 'scale(0.95)' : 'scale(1)')};
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.2);

    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0) ${({ $isScrolled }) => ($isScrolled ? 'scale(0.95)' : 'scale(1)')};
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.15);
  }

  .dark & {
    background: #ffffff;
    border: 1px solid transparent;
    color: #0f172a;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    
    &:hover {
      background: #f1f5f9;
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.35);
    }

    &:active {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.12),
                0 4px 12px rgba(15, 23, 42, 0.15);
  }
`;

const CartCount = styled.span`
  background: rgba(255, 255, 255, 0.95);
  color: #0f172a;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 9999px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  line-height: 1;
  transition: all 0.3s ease;
  flex-shrink: 0;
  
  .dark & {
    background: rgba(255,255,255,0.12);
    color: #ffffff;
    border: 1px solid rgba(255,255,255,0.18);
  }

  @media (max-width: 768px) {
    position: absolute;
    top: -4px;
    right: -4px;
    font-size: 0.625rem;
    font-weight: 700;
    padding: 2px 5px;
    min-width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
    
    .dark & {
      border-color: #0f172a;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    }
  }

  @media (max-width: 480px) {
    top: -3px;
    right: -3px;
    font-size: 0.5625rem;
    padding: 1px 4px;
    min-width: 16px;
    height: 16px;
    border-width: 1.5px;
  }

  @media (max-width: 360px) {
    top: -2px;
    right: -2px;
    font-size: 0.5rem;
    padding: 1px 3px;
    min-width: 14px;
    height: 14px;
  }
`;

const CartButtonText = styled.span<{ $showOnMobile?: boolean }>`
  display: inline;

  @media (max-width: 768px) {
    display: none; // Verberg tekst op mobiel, alleen badge tonen
  }
`;


const Header = () => {
  const { language, setLanguage } = useLanguage();
  const { cartItems } = useContext(CartContext);
  const { goToHome, goToCart } = useContext(NavigationContext);
  const t = (key: string) => getTranslation(language as Language, key);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [isDark, setIsDark] = useState<boolean>(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const lastScrollY = useRef(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const currentLanguage = languages.find(lang => lang.code === language) || languages[0];

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
    setIsLangDropdownOpen(false);
  };

  const toggleLanguageDropdown = () => {
    setIsLangDropdownOpen(!isLangDropdownOpen);
  };

  const handleCartClick = () => {
    if (cartItems.length > 0) {
      goToCart();
    } else {
      // Go to home/device selection when no items
      goToHome();
    }
  };

  const handleHeaderMouseLeave = () => {
    // Close language dropdown when mouse leaves header
    setIsLangDropdownOpen(false);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLangDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Initialize theme state based on current document class
  useEffect(() => {
    const current = document.documentElement.classList.contains('dark');
    setIsDark(current);
    const onStorage = () => {
      const pref = localStorage.getItem('theme');
      if (pref) setIsDark(pref === 'dark');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
  };

  // Scroll detection for sticky navbar effects + hide-on-scroll
  useEffect(() => {
    let ticking = false;

    const update = () => {
      const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      const diff = currentScrollY - lastScrollY.current;

      // Always show at the very top
      if (currentScrollY <= 10) {
        setIsHidden(false);
      } else if (Math.abs(diff) >= 8) {
        // Require minimum 8px scroll to avoid flicker
        setIsHidden(diff > 0);
        lastScrollY.current = currentScrollY;
      }

      setIsScrolled(currentScrollY > 20);
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    };

    // Initial state
    lastScrollY.current = window.pageYOffset || document.documentElement.scrollTop || 0;

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <HeaderContainer onMouseLeave={handleHeaderMouseLeave} $isScrolled={isScrolled} $isHidden={isHidden}>
      <HeaderShell
        ref={headerRef}
        $isLangOpen={isLangDropdownOpen}
        $isScrolled={isScrolled}
      >
        <HeaderContent $isScrolled={isScrolled}>
          <Logo href="#" onClick={(e) => { e.preventDefault(); goToHome(); }} $isScrolled={isScrolled}>
            <LogoImage src="/images/phonebroslogo.png" alt="Phone Bros Logo" $isScrolled={isScrolled} />
            <LogoText $isScrolled={isScrolled}>Phone Bros</LogoText>
          </Logo>

          <Navigation $isScrolled={isScrolled}>
            {/* Dark Mode Toggle - Temporarily disabled and hidden */}
            <ThemeToggleButton
              onClick={toggleTheme}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDark ? 'Light mode' : 'Dark mode'}
              $isScrolled={isScrolled}
              style={{ display: 'none', pointerEvents: 'none' }}
              tabIndex={-1}
              aria-hidden="true"
            >
              {isDark ? (
                // Sun icon
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                </svg>
              ) : (
                // Moon icon
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
                </svg>
              )}
            </ThemeToggleButton>
            <LanguageSelector ref={dropdownRef}>
              <LanguageButton onClick={toggleLanguageDropdown} $isScrolled={isScrolled}>
                <FlagWrapper>
                  <ReactCountryFlag
                    countryCode={currentLanguage.countryCode.toUpperCase()}
                    svg
                    style={{ width: '100%', height: '100%' }}
                    title={currentLanguage.name}
                  />
                </FlagWrapper>
                <DropdownIcon $isOpen={isLangDropdownOpen} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </DropdownIcon>
              </LanguageButton>

              <LanguageDropdown $isOpen={isLangDropdownOpen}>
                {languages.map((lang) => (
                  <LanguageOption
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    selected={lang.code === currentLanguage.code}
                    aria-selected={lang.code === currentLanguage.code}
                    role="option"
                  >
                    <FlagWrapper>
                      <ReactCountryFlag
                        countryCode={lang.countryCode.toUpperCase()}
                        svg
                        style={{ width: '100%', height: '100%' }}
                        title={lang.name}
                      />
                    </FlagWrapper>
                    <LanguageName>{lang.name}</LanguageName>
                    <LanguageCode>({lang.code.toUpperCase()})</LanguageCode>
                  </LanguageOption>
                ))}
              </LanguageDropdown>
            </LanguageSelector>

            <CartButton onClick={handleCartClick} aria-label={cartItems.length > 0 ? t('yourSelection') : t('startSelling')} $isScrolled={isScrolled}>
              <TagIconWrapper>
                <Wrench size={18} strokeWidth={2.5} />
              </TagIconWrapper>
              <CartButtonText $showOnMobile={true}>
                {cartItems.length > 0 ? t('yourSelection') : t('startSelling')}
              </CartButtonText>
              {cartItems.length > 0 && <CartCount>{cartItems.length}</CartCount>}
            </CartButton>
          </Navigation>
        </HeaderContent>
      </HeaderShell>
    </HeaderContainer>
  );
};

export default Header;
